import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Button, CircularProgress, Typography, TextField, IconButton,
} from '@mui/material';
import { ArrowUpward } from '@material-ui/icons';
import { CHAT_TYPE } from '../../../../utils/constants';
import { parseMarkdownSimple } from '../utils/cpa.utils';
import {
    CpaOrgTypeForm, CpaGoalsForm, CpaQuotePrompt, CpaContactForm,
} from './CpaInlineForm.comp';

const CpaChatPanel = ({
    classes,
    chatHistory,
    isStreaming,
    uiState,
    onSubmitOrgType,
    onSubmitGoals,
    onSubmitQuoteResponse,
    onSubmitContactForm,
    onReset,
    onCancelRequest,
    onSendMessage,
}) => {
    const bottomRef = useRef(null);
    const convEndRef = useRef(null);
    const [inputText, setInputText] = useState('');

    const handleSend = () => {
        const text = inputText.trim();
        if (!text || isStreaming || !onSendMessage) return;
        setInputText('');
        onSendMessage(text);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Keep the latest conversation turn visible. When chat messages change (a
    // typed question + the AI reply, or streaming text), scroll to the END OF THE
    // CONVERSATION (which sits just above the step chips) so the user always sees
    // what they typed and the response — instead of the chips covering it.
    useEffect(() => {
        if (convEndRef.current) {
            convEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [chatHistory]);

    // When a new step's chips/form appears, scroll to the very bottom so the
    // options are in view.
    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [uiState.currentForm, uiState.showQuotePrompt]);

    const showCompletion =
        uiState.applicationSubmitted ||
        uiState.applicationComplete ||
        uiState.applicationAlreadySubmitted;

    const showContactForm =
        uiState.currentForm === 'contact_form' && !uiState.contactFormSaved;

    return (
        <Box className={classes.chatPanel}>
            <Box className={classes.chatScroll}>
                {chatHistory.map((msg, idx) => (
                    <Box
                        key={idx}
                        className={
                            msg.type === CHAT_TYPE.USER
                                ? classes.userBubbleWrap
                                : classes.botBubbleWrap
                        }
                    >
                        <Box
                            className={
                                msg.type === CHAT_TYPE.USER ? classes.userBubble : classes.botBubble
                            }
                        >
                            {msg.type === CHAT_TYPE.BOT ? (
                                <span dangerouslySetInnerHTML={{ __html: parseMarkdownSimple(msg.text) }} />
                            ) : (
                                msg.text
                            )}
                        </Box>
                    </Box>
                ))}

                {/* End-of-conversation anchor — sits ABOVE the step chips so the
                    latest typed message + AI reply stay visible. */}
                <div ref={convEndRef} />

                {uiState.currentForm === 'org_type' && !uiState.orgTypeSaved && (
                    <CpaOrgTypeForm
                        classes={classes}
                        disabled={isStreaming}
                        onSubmit={onSubmitOrgType}
                        errorCode={uiState.errorCode}
                    />
                )}
                {uiState.currentForm === 'goals' && !uiState.goalsSaved && (
                    <CpaGoalsForm
                        classes={classes}
                        disabled={isStreaming}
                        onSubmit={onSubmitGoals}
                        errorCode={uiState.errorCode}
                    />
                )}
                {onSendMessage
                    && ((uiState.currentForm === 'org_type' && !uiState.orgTypeSaved)
                        || (uiState.currentForm === 'goals' && !uiState.goalsSaved)) && (
                    <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: '#9aa0a6', fontStyle: 'italic' }}
                    >
                        👆 Pick an option above, or type a question below — ask me anything.
                    </Typography>
                )}
                {uiState.showQuotePrompt && !showCompletion && (
                    <CpaQuotePrompt
                        disabled={isStreaming}
                        onYes={onSubmitQuoteResponse}
                        onNo={onSubmitQuoteResponse}
                    />
                )}
                {showContactForm && (
                    <CpaContactForm
                        disabled={isStreaming}
                        onSubmit={onSubmitContactForm}
                        errorCode={uiState.errorCode}
                    />
                )}

                {isStreaming && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="caption" color="textSecondary">Thinking…</Typography>
                    </Box>
                )}

                {uiState.applicationSubmitted && (
                    <Box className={classes.thankYouBox}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0097B2' }}>
                            Thank you for your submission!
                        </Typography>
                        {uiState.applicationCode && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Reference: <strong>{uiState.applicationCode}</strong>
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            We will reach out within 2–3 business days with an official quote and project timeline.
                        </Typography>
                    </Box>
                )}

                {uiState.applicationComplete && !uiState.applicationSubmitted && (
                    <Box className={classes.thankYouBox}>
                        <Typography variant="body1">Thank you for exploring Powered by DQ.</Typography>
                    </Box>
                )}

                {uiState.applicationAlreadySubmitted && uiState.applicationCode && (
                    <Box className={classes.thankYouBox}>
                        <Typography variant="body1">
                            You have already submitted enquiry <strong>{uiState.applicationCode}</strong>.
                        </Typography>
                    </Box>
                )}

                {showCompletion && onReset && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            disabled={isStreaming}
                            onClick={onReset}
                            sx={{
                                textTransform: 'none',
                                borderColor: '#FF6900',
                                color: '#FF6900',
                                '&:hover': { borderColor: '#E65C00', bgcolor: '#FFF3E9' },
                            }}
                        >
                            Submit a new enquiry
                        </Button>
                    </Box>
                )}

                <div ref={bottomRef} />
            </Box>

            {/* Free-text question box — users can ask anything; the AI answers.
                The structured steps above still drive the actual enquiry. */}
            {onSendMessage && !showCompletion && (
                <Box sx={{ borderTop: '1px solid var(--brand-border, #e0dcdc)', p: 1.5, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                        placeholder="Type something here"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isStreaming}
                        inputProps={{ maxLength: 1000 }}
                    />
                    <IconButton
                        onClick={handleSend}
                        disabled={isStreaming || !inputText.trim()}
                        sx={{
                            bgcolor: '#FF6900', color: '#fff', flexShrink: 0,
                            '&:hover': { bgcolor: '#E65C00' },
                            '&.Mui-disabled': { bgcolor: '#f0c3a3', color: '#fff' },
                        }}
                        size="small"
                    >
                        {isStreaming ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <ArrowUpward fontSize="small" />}
                    </IconButton>
                </Box>
            )}

            {!showCompletion && onCancelRequest && (
                <Box sx={{ px: 1.5, pb: 1.2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="text"
                        disabled={isStreaming}
                        onClick={onCancelRequest}
                        sx={{
                            textTransform: 'none',
                            color: '#9aa0a6',
                            fontWeight: 600,
                            '&:hover': { color: '#6b7280', background: 'transparent', textDecoration: 'underline' },
                        }}
                    >
                        Cancel request
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default CpaChatPanel;
